import {ChangeDetectorRef, Component, DestroyRef, Input, OnInit} from '@angular/core'
import {TypedFormControls, TypedFormGroup} from '../../../../../../core/forms/form-controls.model'
import {
  PriceRange,
  RenewableEnergyConsulting_BoilerSystem,
  RenewableEnergyConsulting_BuildingType,
  RenewableEnergyConsulting_HeatingSystem,
  RenewableEnergyConsulting_HeatingSystemAge,
  RenewableEnergyConsulting_HeatingType,
  RenewableEnergyConsulting_NumberOfApartments,
  RenewableEnergyConsultingQuestionsInput,
  StaticJobQuestionsInput,
} from '../../../../../../../__generated__/types'
import {StaticComponentInstance, StaticFormLayoutConfig} from '../../static-form/static-form.types'
import {UntypedFormControl, Validators} from '@angular/forms'
import {conditionalValidator} from '../../../../../../craft/forms/form.types'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {BehaviorSubject, Observable} from 'rxjs'
import {TranslocoService} from '@ngneat/transloco'

@Component({
  selector: 'bgo-renewable-energy-consulting-form',
  templateUrl: './renewable-energy-consulting-form.component.html',
  styleUrls: ['../../gradual-reveal.css'],
  standalone: false,
})
export class RenewableEnergyConsultingFormComponent implements OnInit, StaticComponentInstance {
  @Input() form!: TypedFormGroup<RenewableEnergyConsultingQuestionsInput>

  protected priceRange$?: Observable<PriceRange>

  readonly HeatingType = RenewableEnergyConsulting_HeatingType
  readonly HeatingSystem = RenewableEnergyConsulting_HeatingSystem
  readonly HeatingSystemAge = RenewableEnergyConsulting_HeatingSystemAge
  readonly BoilerSystem = RenewableEnergyConsulting_BoilerSystem
  readonly BuildingType = RenewableEnergyConsulting_BuildingType
  readonly NumberOfApartments = RenewableEnergyConsulting_NumberOfApartments

  private readonly _layout$ = new BehaviorSubject<StaticFormLayoutConfig>({})

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly translationService: TranslocoService,
    private readonly destroyRef: DestroyRef,
  ) {}

  public static createForm(): TypedFormGroup<RenewableEnergyConsultingQuestionsInput> {
    const recentRenovations = new UntypedFormControl(undefined, [Validators.required])
    const buildingType = new UntypedFormControl(undefined, [Validators.required])

    const controls: TypedFormControls<Omit<RenewableEnergyConsultingQuestionsInput, '__typename'>> = {
      boilerSystem: new UntypedFormControl(undefined, [Validators.required]),
      buildingOccupants: new UntypedFormControl(undefined, [Validators.required]),
      buildingType,
      constructionYear: new UntypedFormControl(undefined, [Validators.required, Validators.min(1800)]),
      heatingEnergyConsumption: new UntypedFormControl(undefined, [Validators.required]),
      heatedAreaSquareMeters: new UntypedFormControl(undefined, [Validators.required]),
      heatingSystem: new UntypedFormControl(undefined, [Validators.required]),
      heatingSystemAge: new UntypedFormControl(undefined, [Validators.required]),
      heatingType: new UntypedFormControl(undefined, [Validators.required]),
      numberOfApartments: new UntypedFormControl(
        undefined,
        conditionalValidator(
          () => buildingType.value === RenewableEnergyConsulting_BuildingType.MultiFamily,
          [Validators.required],
        ),
      ),
      recentRenovations,
      recentRenovationsDescription: new UntypedFormControl(
        undefined,
        conditionalValidator(() => recentRenovations.value, [Validators.required]),
      ),
    }

    return new TypedFormGroup<RenewableEnergyConsultingQuestionsInput>(controls)
  }

  ngOnInit(): void {
    this.observeForm()
    this.observeRecentRenovations()
  }

  layout$() {
    return this._layout$.asObservable()
  }

  questionsInput(): StaticJobQuestionsInput {
    const value = this.form?.value
    return {
      renewableEnergyConsulting: {
        heatingSystemAge: value?.heatingSystemAge ?? RenewableEnergyConsulting_HeatingSystemAge.MoreThan_10Years,
        heatedAreaSquareMeters: value?.heatedAreaSquareMeters
          ? Number.parseFloat(value.heatedAreaSquareMeters.toString())
          : 0,
        boilerSystem: value?.boilerSystem ?? RenewableEnergyConsulting_BoilerSystem.Unknown,
        buildingOccupants: value?.buildingOccupants ? Number.parseInt(value.buildingOccupants.toString()) : 0,
        buildingType: value?.buildingType ?? RenewableEnergyConsulting_BuildingType.SingleFamily,
        heatingEnergyConsumption: value?.heatingEnergyConsumption
          ? Number.parseFloat(value.heatingEnergyConsumption.toString())
          : 0,
        constructionYear: value?.constructionYear ? Number.parseInt(value.constructionYear.toString()) : 0,
        heatingSystem: value?.heatingSystem ?? RenewableEnergyConsulting_HeatingSystem.Unknown,
        heatingType: value?.heatingType ?? RenewableEnergyConsulting_HeatingType.Other,
        numberOfApartments: value?.numberOfApartments ?? RenewableEnergyConsulting_NumberOfApartments.SixOrLess,
        recentRenovations: value?.recentRenovations ?? false,
        recentRenovationsDescription: value?.recentRenovationsDescription,
      },
    }
  }

  setPriceRange(priceRange$: Observable<PriceRange>) {
    this.priceRange$ = priceRange$
    this.observePriceRange(priceRange$)
  }

  private observePriceRange(priceRange$: Observable<PriceRange>) {
    priceRange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(priceRange => {
      this._layout$.next({
        infoText: priceRange.freeOfCharge
          ? this.translationService.translate('createJobRequest.renewableEnergyConsultingForm.priceRangeInfoText')
          : undefined,
      })
    })
  }

  private observeForm() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.form.controls.recentRenovationsDescription.updateValueAndValidity({emitEvent: false})
      this.form.controls.numberOfApartments?.updateValueAndValidity({emitEvent: false})
      this.cdr.detectChanges()
    })
  }

  private observeRecentRenovations() {
    this.form.controls.recentRenovations.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value) {
        this.form.controls.recentRenovationsDescription?.enable()
      } else {
        this.form.controls.recentRenovationsDescription?.disable()
        this.form.controls.recentRenovationsDescription?.setValue(undefined)
      }
    })
  }
}
